/**
 * README PLEASE
 *
 * The PlayerStateRepository exposes methods to set the state, and subscribe to state changes.
 * The state field is readonly.
 *
 * The state is structured as a tree of nodes starting from the root node. Each node should
 * be self contained in its own file and should adhere to the following structure.
 *
 *  - Define a type for the data of that particular node. This type should be exported.
 *  - Define a handler class.
 *      - To maintain a standard, the name should be {{type name}}Handler.
 *        So if the type is defined as PlayerState, the handler class should be PlayerStateHandler.
 *        The handler class should implement the IPlayerStateHandler.
 *
 * The state getter, in the handler, should take care to call cloneDeep() on any portions of the state
 * that would be passed by reference (this is not necessary for portions that are handled by child nodes).
 * This is because the PlayerStateRepository class does not call cloneDeep() on the entire state. However,
 * we make a copy of the state before setting the state (for the previousState field that is emitted),
 * and that copy needs to be unchanged after we set the new state.
 */

import EventBus, { Event, Handler, Unsubscriber } from '../event-bus';
import PlayerStateHandler, { PlayerState } from './player-state.root';

export type {
    SubtitleTrack,
    LevelDetails
} from './interfaces-and-types';

export type { PlayerState };

type UnsubscriberAndState = {
    unsubscriber: Unsubscriber,
    state: PlayerState
}

export type StateHandler = (
    partial: Partial<PlayerState>,
    previousState: PlayerState,
    context: string
) => void

export default class PlayerStateRepository {
    private rootStateHandler = new PlayerStateHandler();

    constructor(
        private eventBus = new EventBus()
    ) { }

    public onStateChange(handler: StateHandler): UnsubscriberAndState {
        const stateHandler = (
            { partial, previousState }: { partial: Partial<PlayerState>, previousState: PlayerState },
            context: string
        ) => {
            handler(partial, previousState, context);
        };

        const unsubscriber = this.eventBus.on(
            Event.stateChange,
            stateHandler as Handler
        );

        return {
            unsubscriber,
            state: this.state
        };
    }

    private emitState(
        partial: Partial<PlayerState>,
        previousState: PlayerState,
        context?: string
    ) {
        this.eventBus.emit(Event.stateChange, {
            partial,
            previousState
        }, context);
    }

    // state setters

    public setState(partial: Partial<PlayerState>, context?: string): void {
        const previousState = this.state;

        const changedPartialState = this.rootStateHandler.setState(partial);

        this.emitState(changedPartialState, previousState, context);
    }

    // public getters

    public get state(): PlayerState {
        return this.rootStateHandler.state;
    }
}
